import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { grey } from '@mui/material/colors'
import { alpha, useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import { AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps'
import noop from 'lodash/noop'
import React, { useCallback, useMemo } from 'react'

import { ReactComponent as ToterIcon } from 'assets/inventory/container-type/Toter.svg'
import { isPlacement, isCustomerOwned } from 'components/inventory/containers/utils'
import { TYPE_TO_ICON_MAP } from 'components/settings/pages/inventory/container-types/settings'
import { CONTAINER_STATUS, CONTAINER_STATUS_META } from 'containers/inventory/settings'
import { UNASSIGNED } from 'theme/colors'
import { getFormattedColorAndContrastColor } from 'theme/utils'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'

import type { Theme } from '@mui/material/styles'
import type { ContainerMarkerProperties } from 'components/common/map/webgl/containers/containerMarkerColors'

// What this marker reads off a container row, on top of what the colour resolver needs.
type MarkerContainer = ContainerMarkerProperties & {
  latitude?: number | string | null
  longitude?: number | string | null
  containerName?: string | null
  containerType?: { type?: string | null } | null
  measure?: { volume?: number | string | null; unit?: string | null } | null
}

type ContainerMarkerProps = {
  container: MarkerContainer
  showContainerName?: boolean
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>, container: MarkerContainer) => void
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void
  draggable?: boolean
  onDragEnd?: (event: unknown) => void
  onClick?: (event: unknown, container: MarkerContainer) => void
  selected?: boolean
}

const MARKER_SX = {
  transition: 'z-index 0.15s',
  transformOrigin: 'bottom center',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}

// The pointer tail under the chip; only its border colour varies.
const TAIL_SX = {
  bottom: '-8px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '7px solid transparent',
  borderRight: '7px solid transparent',
  zIndex: -1,
}

const getProps = (container: MarkerContainer, theme: Theme) => {
  const color = get(container, 'color.color')
  const { contrastColor } = getFormattedColorAndContrastColor(color, theme)
  const containerStatus = get(container, 'status')
  const containerStatusMeta = get(CONTAINER_STATUS_META, containerStatus)
  // Mirrors resolveContainerColors: a placement has no status to colour the border by.
  const placement = isPlacement(container)
  const containerStatusColor = placement ? grey[500] : get(containerStatusMeta, 'color', UNASSIGNED.main)
  const isDecommissioned = containerStatus === CONTAINER_STATUS.DECOMMISSIONED

  if (isDecommissioned) {
    return {
      chipTextColor: theme.palette.common.white,
      chipBgColor: grey[700],
      chipBorderColor: containerStatusColor,
      chipBorderStyle: placement ? 'dashed' : 'solid',
      iconBgColor: '',
      iconColor: theme.palette.common.white,
    }
  }

  return {
    chipTextColor: theme.palette.common.black,
    chipBgColor: theme.palette.common.white,
    chipBorderColor: containerStatusColor,
    chipBorderStyle: placement ? 'dashed' : 'solid',
    iconBgColor: color,
    iconColor: color ? contrastColor : theme.palette.action.active,
  }
}

const ContainerMarker = ({
  container,
  showContainerName = false,
  onMouseEnter = noop,
  onMouseLeave = noop,
  draggable = false,
  onDragEnd = noop,
  onClick = noop,
  selected = false,
}: ContainerMarkerProps) => {
  const theme = useTheme()
  const { chipTextColor, chipBgColor, chipBorderColor, chipBorderStyle, iconBgColor, iconColor } = useMemo(
    () => getProps(container, theme),
    [container, theme]
  )

  const handleClick = useCallback((e: unknown) => onClick(e, container), [onClick, container])
  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => onMouseEnter(e, container), [onMouseEnter, container])
  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLElement>) => onMouseLeave(e), [onMouseLeave])

  const chipStyles = {
    border: `2px ${chipBorderStyle}`,
    borderColor: chipBorderColor,
    borderRadius: 4,
    color: chipTextColor,
    backgroundColor: chipBgColor,
    ...theme.typography.h6,
    fontWeight: 700,
  }
  const type = useMemo(() => get(container, 'containerType.type'), [container])
  const measure = useMemo(() => get(container, 'measure'), [container])
  const measureLabel = useMemo(() => (measure ? `${measure.volume} ${getMeasureUnit(measure.unit)}` : ''), [measure])
  const Icon = get(TYPE_TO_ICON_MAP, type, ToterIcon)
  // Whose it is, on the axis the border cannot carry: status already owns the border.
  const customerOwned = isCustomerOwned(container)

  const containerName = useMemo(() => {
    // Maintain backward compatibility with the old container flow
    const name = get(container, 'containerName', '') || get(container, 'name', '')
    const match = name?.match(/#(\d+)/)
    return match ? match[1] : name
  }, [container])
  const chipLabel = useMemo(
    () => (showContainerName ? `${measureLabel} #${containerName}` : measureLabel || containerName),
    [measureLabel, containerName, showContainerName]
  )
  const position = useMemo(
    () => ({ lat: Number(container.latitude), lng: Number(container.longitude) }),
    [container.latitude, container.longitude]
  )

  return (
    <AdvancedMarker
      anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM}
      draggable={draggable}
      position={position}
      zIndex={selected ? 1000 : 10}
      onClick={handleClick}
      onDragEnd={onDragEnd}
    >
      <Box position="relative" sx={MARKER_SX} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Chip
          label={chipLabel?.trim()}
          {...(customerOwned && { icon: <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /> })}
          sx={{
            ...chipStyles,
            ...(selected && {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.7)}`,
            }),
          }}
          {...(Icon && {
            deleteIcon: (
              <Box alignItems="center" display="flex" justifyContent="center" {...(iconBgColor && { bgcolor: iconBgColor })}>
                <SvgIcon fontSize="small" sx={{ ...(iconColor && { color: iconColor }) }}>
                  <Icon />
                </SvgIcon>
              </Box>
            ),
            onDelete: noop,
          })}
        />
        <Box
          sx={{
            position: 'absolute',
            ...TAIL_SX,
            borderTop: `7px solid ${chipBorderColor}`,
          }}
        />
      </Box>
    </AdvancedMarker>
  )
}
export default ContainerMarker
